import type { AnnounceOutboxRecord, OutboxRecord, OutboxStore, PhotoOutboxRecord } from './store';

/**
 * A ~90-line IndexedDB wrapper, on purpose.
 *
 * The outbox needs exactly one thing from IndexedDB that `localStorage` cannot give it:
 * storing `Blob`s of a few hundred KB, durably, without base64-inflating them by a third.
 * That is four operations against one object store — importing a wrapper library for it
 * would be more code to audit than the code it replaces, and photo bytes waiting to be
 * posted are the one thing in this app we cannot afford to lose to a clever abstraction.
 *
 * Yjs state uses `y-indexeddb` and is none of this file's business; this database only
 * ever holds *unsent* things.
 */

export const OUTBOX_DB_NAME = 'awwyeah-checkin-outbox';
export const OUTBOX_DB_VERSION = 1;
export const PHOTO_STORE = 'photos';
export const ANNOUNCE_STORE = 'announcements';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

export function openOutboxDb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = factory.open(OUTBOX_DB_NAME, OUTBOX_DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      for (const name of [PHOTO_STORE, ANNOUNCE_STORE]) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: 'id' });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('IndexedDB open failed'));
  });
}

export class IdbOutboxStore<T extends OutboxRecord> implements OutboxStore<T> {
  /** The database handle is opened once and shared; every call awaits the same promise. */
  private readonly db: Promise<IDBDatabase>;

  constructor(
    private readonly storeName: string,
    db: Promise<IDBDatabase>,
  ) {
    this.db = db;
  }

  private async tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.db;
    return db.transaction(this.storeName, mode).objectStore(this.storeName);
  }

  async put(record: T): Promise<void> {
    const store = await this.tx('readwrite');
    await request(store.put(record));
  }

  async get(id: string): Promise<T | undefined> {
    const store = await this.tx('readonly');
    return (await request(store.get(id))) as T | undefined;
  }

  async all(): Promise<T[]> {
    const store = await this.tx('readonly');
    return (await request(store.getAll())) as T[];
  }

  async delete(id: string): Promise<void> {
    const store = await this.tx('readwrite');
    await request(store.delete(id));
  }
}

export interface OutboxStores {
  photos: OutboxStore<PhotoOutboxRecord>;
  announcements: OutboxStore<AnnounceOutboxRecord>;
}

/** Opens both outbox stores, or `null` when IndexedDB is unusable (private mode, denied). */
export async function openOutboxStores(
  factory: IDBFactory | undefined = typeof indexedDB === 'undefined' ? undefined : indexedDB,
): Promise<OutboxStores | null> {
  if (!factory) return null;
  try {
    const db = openOutboxDb(factory);
    await db;
    return {
      photos: new IdbOutboxStore(PHOTO_STORE, db),
      announcements: new IdbOutboxStore(ANNOUNCE_STORE, db),
    };
  } catch (error) {
    console.warn('[checkin] IndexedDB unavailable — photos will only survive this session', error);
    return null;
  }
}
